import type React from 'react'
import { useState } from 'react'
import { useIntl } from 'react-intl'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { captureException } from '@sentry/react'

import { AnimatedBox } from '@/components/atoms/AnimatedBox'
import { Button } from '@/components/atoms/Button/Button.tsx'
import { Car, type CarProps } from '@/components/atoms/Car/Car.tsx'
import { Dialog, DialogTrigger } from '@/components/atoms/Dialog/Dialog.tsx'
import { Icons } from '@/components/atoms/Icons/Icons'
import { MessageImage } from '@/components/molecules/MessageImage'
import { cn } from '@/lib/utils'

export interface ChannelMessageDeletionTrace {
  text?: string
  firebaseStoragePath?: string
  imageWidth?: number
  imageHeight?: number
  deletedByLabel: string
  deletedAt: Date
}

export interface ChannelMessageCardProps {
  sender: 'volunteer' | 'admin'
  senderLabel: string
  createdAt: Date
  text?: string
  firebaseStoragePath?: string
  imageWidth?: number
  imageHeight?: number
  car?: Pick<CarProps, 'state' | 'carNumber' | 'carType'>
  deleted: boolean
  deletion?: ChannelMessageDeletionTrace
  canDelete: boolean
  onDelete?: () => Promise<void> | void
}

export const ChannelMessageCard: React.FC<ChannelMessageCardProps> = ({
  sender,
  senderLabel,
  createdAt,
  text,
  firebaseStoragePath,
  imageWidth,
  imageHeight,
  car,
  deleted,
  deletion,
  canDelete,
  onDelete,
}) => {
  const intl = useIntl()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteFailed, setDeleteFailed] = useState(false)
  const fromAdmin = sender === 'admin'
  const body = (
    <MessageBody
      deleted={deleted}
      text={text}
      image={
        firebaseStoragePath ? { storagePath: firebaseStoragePath, width: imageWidth, height: imageHeight } : undefined
      }
      fromAdmin={fromAdmin}
      deletion={deletion}
    />
  )

  const handleDelete = async () => {
    setDeleting(true)
    setDeleteFailed(false)
    try {
      await onDelete?.()
      setConfirmOpen(false)
    } catch (error) {
      captureException(error)
      setDeleteFailed(true)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <AnimatedBox>
      <div
        data-testid="channel-message"
        className={cn(
          'grid items-baseline gap-1 border-t border-gray-200 py-5 text-base',
          fromAdmin ? 'grid-cols-[1fr_6rem]' : 'grid-cols-[6rem_1fr]'
        )}>
        {fromAdmin ? (
          <>
            <div className="text-right font-bold select-none">{senderLabel}</div>
            <p className="justify-self-center text-sm text-gray-900 select-none">{intl.formatTime(createdAt)}</p>
            <div className="flex w-full min-w-0 flex-col items-end gap-3 self-start pl-6">
              {body}
              {canDelete && !deleted && onDelete && (
                <DeleteControl
                  open={confirmOpen}
                  onOpenChange={setConfirmOpen}
                  deleting={deleting}
                  failed={deleteFailed}
                  onConfirm={handleDelete}
                />
              )}
            </div>
            <div className="justify-self-center">
              <Icons.ONR />
            </div>
          </>
        ) : (
          <>
            <p className="justify-self-center text-sm text-gray-900 select-none">{intl.formatTime(createdAt)}</p>
            <div className="font-bold select-none">{senderLabel}</div>
            <div className="justify-self-center">{car && <Car {...car} />}</div>
            <div className="flex w-full min-w-0 flex-col items-start gap-3 self-start pr-6">
              {body}
              {canDelete && !deleted && onDelete && (
                <DeleteControl
                  open={confirmOpen}
                  onOpenChange={setConfirmOpen}
                  deleting={deleting}
                  failed={deleteFailed}
                  onConfirm={handleDelete}
                />
              )}
            </div>
          </>
        )}
      </div>
    </AnimatedBox>
  )
}

interface MessageBodyImage {
  storagePath: string
  width?: number
  height?: number
}

const MessageBody: React.FC<{
  deleted: boolean
  text?: string
  image?: MessageBodyImage
  fromAdmin: boolean
  deletion?: ChannelMessageDeletionTrace
}> = ({ deleted, text, image, fromAdmin, deletion }) => {
  const intl = useIntl()
  if (deleted) {
    return (
      <div className={cn('flex w-full flex-col gap-1 text-gray-500 italic', fromAdmin && 'items-end text-right')}>
        <div className="text-lg">{intl.formatMessage({ id: 'channel.message.deleted' })}</div>
        {deletion && (
          <>
            {deletion.text && (
              <div data-testid="channel-message-original" className="w-full text-lg break-words whitespace-pre-wrap">
                {deletion.text}
              </div>
            )}
            {deletion.firebaseStoragePath && (
              <MessageImage
                storagePath={deletion.firebaseStoragePath}
                width={deletion.imageWidth}
                height={deletion.imageHeight}
                dimmed
                // The deletion revoked every download URL fetched before it, the one cached for the
                // live message included.
                queryScope={`deletion-${deletion.deletedAt.getTime()}`}
              />
            )}
            <div data-testid="channel-message-deleted-by" className="text-sm">
              {intl.formatMessage(
                { id: 'channel.message.deletedBy' },
                { name: deletion.deletedByLabel, time: intl.formatTime(deletion.deletedAt) }
              )}
            </div>
          </>
        )}
      </div>
    )
  }
  // Volunteers type on a phone: line breaks carry meaning, and a pasted link has no space to wrap
  // on. The full width that buys costs the flex alignment, so the side is set on the text itself.
  return (
    <>
      {text && (
        <div
          data-testid="channel-message-text"
          className={cn('w-full text-lg break-words whitespace-pre-wrap', fromAdmin && 'text-right')}>
          {text}
        </div>
      )}
      {image && <MessageImage storagePath={image.storagePath} width={image.width} height={image.height} />}
    </>
  )
}

interface DeleteControlProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  deleting: boolean
  failed: boolean
  onConfirm: () => Promise<void>
}

const DeleteControl: React.FC<DeleteControlProps> = ({ open, onOpenChange, deleting, failed, onConfirm }) => {
  const intl = useIntl()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="channel-message-delete"
          aria-label={intl.formatMessage({ id: 'channel.delete.title' })}
          className="cursor-pointer rounded p-1 hover:bg-gray-100">
          <Icons.trash className="size-4 [&_path]:fill-gray-500" />
        </button>
      </DialogTrigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content className="fixed top-[50%] left-[50%] z-50 flex w-full max-w-[520px] translate-x-[-50%] translate-y-[-50%] flex-col gap-6 rounded-[20px] bg-white p-6 shadow-lg">
          <DialogPrimitive.Title className="text-xl font-normal text-gray-800">
            {intl.formatMessage({ id: 'channel.delete.title' })}
          </DialogPrimitive.Title>
          <p className="text-base text-gray-600">{intl.formatMessage({ id: 'channel.delete.description' })}</p>
          {failed && (
            <p data-testid="channel-message-delete-error" className="text-base text-red-600">
              {intl.formatMessage({ id: 'channel.delete.failed' })}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <DialogPrimitive.Close asChild>
              <Button variant="default">{intl.formatMessage({ id: 'channel.delete.cancel' })}</Button>
            </DialogPrimitive.Close>
            <Button
              data-testid="channel-message-delete-confirm"
              variant="critical"
              loading={deleting}
              disabled={deleting}
              onClick={onConfirm}>
              {intl.formatMessage({ id: 'channel.delete.confirm' })}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  )
}
